import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  alpha,
  Stack,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import StarIcon from '@mui/icons-material/Star';
import agent from '../api/axios';
import { useAuth } from '../auth/AuthContext';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchSubscriptionPlans,
  fetchUserSubscription,
  cancelSubscription as cancelSubscriptionThunk,
  reactivateSubscription as reactivateSubscriptionThunk,
  changeSubscription as changeSubscriptionThunk,
} from '../store/subscriptionsSlice';
import { PageHeader, LoadingState } from '../components/ui';

export default function SubscriptionManagement() {
  const { enqueueSnackbar } = useSnackbar();
  const { checkSubscription, userRole } = useAuth();
  const dispatch = useAppDispatch();
  const {
    plans: availablePlans,
    userSubscription: subscription,
    loading,
  } = useAppSelector((state) => state.subscriptions);

  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    'cancel' | 'reactivate' | 'change' | null
  >(null);
  const [processing, setProcessing] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [prorationPreview, setProrationPreview] = useState<any>(null);

  const fetchSubscription = async () => {
    try {
      await dispatch(fetchUserSubscription()).unwrap();
    } catch (err: any) {
      console.error('Failed to load subscription', err);
      setError(err.message || 'Failed to load subscription information');
    }
  };

  useEffect(() => {
    fetchSubscription();
    dispatch(fetchSubscriptionPlans());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleCancelSubscription = async () => {
    setProcessing(true);
    try {
      await dispatch(cancelSubscriptionThunk()).unwrap();
      setConfirmDialogOpen(false);
      enqueueSnackbar('Subscription cancelled successfully', {
        variant: 'success',
      });
      await fetchSubscription();
    } catch (err: any) {
      console.error('Cancel failed', err);
      enqueueSnackbar(err.message || 'Failed to cancel subscription', {
        variant: 'error',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setProcessing(true);
    try {
      const result = await dispatch(reactivateSubscriptionThunk()).unwrap();
      setConfirmDialogOpen(false);

      // Check if payment is required
      if (result.requiresPayment) {
        enqueueSnackbar(
          `${result.message}. Your subscription requires payment. Please contact support or add a payment method.`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(
          result.message || 'Subscription reactivated successfully',
          { variant: 'success' }
        );
      }

      await fetchSubscription();

      // Only refresh auth context if subscription is actually active
      if (result.status === 'active') {
        await checkSubscription();
      }
    } catch (err: any) {
      console.error('Reactivate failed', err);
      enqueueSnackbar(err.message || 'Failed to reactivate subscription', {
        variant: 'error',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleChangeSubscription = async () => {
    if (!selectedPlanId) return;

    setProcessing(true);
    try {
      const result = await dispatch(
        changeSubscriptionThunk(selectedPlanId)
      ).unwrap();
      setConfirmDialogOpen(false);
      setChangePlanDialogOpen(false);

      // Check if this is a scheduled downgrade
      if (result.isScheduledDowngrade) {
        enqueueSnackbar(result.message, {
          variant: 'info',
          autoHideDuration: 8000,
        });
      } else {
        // Show detailed result with proration info
        const message = `${result.message}. Current Plan: ${
          result.currentPlan
        } ($${result.currentPrice}/month). New Plan: ${result.newPlan} ($${
          result.newPrice
        }/month). ${
          result.isUpgrade ? 'Charged Today' : 'Credit Applied'
        }: $${Math.abs(result.prorationAmount).toFixed(2)}`;

        enqueueSnackbar(message, { variant: 'success' });
      }

      await fetchSubscription();
      // Refresh auth context in case subscription status changed
      await checkSubscription();
    } catch (err: any) {
      console.error('Change subscription failed', err);
      enqueueSnackbar(err.message || 'Failed to change subscription', {
        variant: 'error',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelScheduledDowngrade = async () => {
    setProcessing(true);
    try {
      const result = await agent.Business.cancelScheduledDowngrade();
      enqueueSnackbar(result.message, { variant: 'success' });
      await fetchSubscription();
    } catch (err: any) {
      console.error('Cancel scheduled downgrade failed', err);
      enqueueSnackbar(err.message || 'Failed to cancel scheduled downgrade', {
        variant: 'error',
      });
    } finally {
      setProcessing(false);
    }
  };

  const calculateProrationPreview = () => {
    // Return the preview fetched from backend
    return prorationPreview;
  };

  const handlePlanSelection = async (planId: string) => {
    setSelectedPlanId(planId);
    setProrationPreview(null); // Reset preview when changing selection

    // Fetch preview from backend
    if (planId) {
      try {
        const preview = await agent.Business.previewSubscriptionChange(planId);
        setProrationPreview(preview);
      } catch (error) {
        console.error('Failed to fetch proration preview', error);
      }
    }
  };

  const openChangePlanDialog = () => {
    setChangePlanDialogOpen(true);
  };

  const openConfirmDialog = (action: 'cancel' | 'reactivate') => {
    setConfirmAction(action);
    setConfirmDialogOpen(true);
  };

  const handleConfirm = () => {
    if (confirmAction === 'cancel') {
      handleCancelSubscription();
    } else if (confirmAction === 'reactivate') {
      handleReactivateSubscription();
    } else if (confirmAction === 'change') {
      handleChangeSubscription();
    }
  };

  const selectedPlan = availablePlans.find((p) => p.id === selectedPlanId);
  const currentPlan = availablePlans.find(
    (p) => p.name === subscription?.planName
  );
  const prorationPreviewData = calculateProrationPreview();

  if (loading) {
    return <LoadingState message="Loading subscription information..." />;
  }

  // Admin users don't need subscriptions
  if (userRole === 'Admin') {
    return (
      <Box className="fade-in" sx={{ maxWidth: 800, m: 'auto' }}>
        <PageHeader
          title="Subscription"
          subtitle="Manage your subscription plan"
          icon={<ReceiptLongIcon />}
        />
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <StarIcon sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
              Admin Account
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
              Full Access - No Subscription Required
            </Typography>
          </Box>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              As an administrator, you have unrestricted access to all features
              and functionality without needing a subscription.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="fade-in" sx={{ maxWidth: 800, m: 'auto' }}>
        <PageHeader
          title="Subscription"
          subtitle="Manage your subscription plan"
          icon={<ReceiptLongIcon />}
        />
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!subscription?.hasSubscription) {
    return (
      <Box className="fade-in" sx={{ maxWidth: 800, m: 'auto' }}>
        <PageHeader
          title="Subscription"
          subtitle="Manage your subscription plan"
          icon={<ReceiptLongIcon />}
        />
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CancelIcon sx={{ fontSize: 40, color: 'warning.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }} gutterBottom>
              No Active Subscription
            </Typography>
            <Typography color="text.secondary">
              {subscription?.message ||
                'You do not have an active subscription.'}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const periodEnd = subscription.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'N/A';

  const isActive =
    subscription.status === 'active' && !subscription.cancelAtPeriodEnd;
  const isCanceling =
    subscription.cancelAtPeriodEnd || subscription.status === 'canceled';
  const isCanceled = subscription.status === 'canceled';
  const isIncomplete = subscription.status === 'incomplete';

  return (
    <Box className="fade-in" sx={{ maxWidth: 800, m: 'auto' }}>
      <PageHeader
        title="Manage Subscription"
        subtitle="View and manage your subscription plan"
        icon={<ReceiptLongIcon />}
      />

      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Plan Header */}
        <Box
          sx={{
            background: isActive
              ? 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)'
              : isCanceling
              ? 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
              : 'linear-gradient(135deg, #9E9E9E 0%, #616161 100%)',
            p: 3,
            color: 'white',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.9 }}>
                Current Plan
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {subscription.planName || 'N/A'}
              </Typography>
            </Box>
            <Chip
              label={subscription.status?.toUpperCase()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
              }}
              icon={
                isActive ? (
                  <CheckCircleIcon sx={{ color: 'white !important' }} />
                ) : isCanceling ? (
                  <CancelIcon sx={{ color: 'white !important' }} />
                ) : undefined
              }
            />
          </Stack>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Price
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ${subscription.planPrice?.toFixed(2) || '0.00'}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  {' '}
                  / {subscription.term || 'month'}
                </Typography>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Billing Period Ends
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {periodEnd}
              </Typography>
            </Box>
          </Stack>

          {(isCanceling || isCanceled) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Subscription Cancelled</strong>
              <Typography variant="body2">
                {isCanceled
                  ? 'Your subscription has been cancelled. Click "Reactivate Subscription" below to restore your access.'
                  : `Your subscription will remain active until ${periodEnd}. No refund will be issued for the current period. You can reactivate anytime before this date.`}
              </Typography>
            </Alert>
          )}

          {subscription.pendingDowngrade && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleCancelScheduledDowngrade}
                  disabled={processing}
                >
                  {processing ? 'Cancelling...' : 'Keep Yearly'}
                </Button>
              }
            >
              <strong>Downgrade Scheduled</strong>
              <Typography variant="body2">
                Your plan will change to {subscription.pendingPlanName} ($
                {subscription.pendingPlanPrice?.toFixed(2)}/month) on{' '}
                {new Date(
                  subscription.pendingChangeDate || ''
                ).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                . You can cancel this change to keep your yearly plan.
              </Typography>
            </Alert>
          )}

          {isIncomplete && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Payment Required</strong>
              <Typography variant="body2">
                Your subscription requires payment to become active. Please
                update your payment method in Stripe or contact support for
                assistance.
              </Typography>
            </Alert>
          )}

          {isActive && !isCanceling && !isIncomplete && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Your subscription will automatically renew on {periodEnd}.
            </Alert>
          )}

          <Box display="flex" gap={2} mt={3}>
            {isActive && !isCanceling && !isCanceled && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={openChangePlanDialog}
                  disabled={processing}
                >
                  Change Plan
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => openConfirmDialog('cancel')}
                  disabled={processing}
                >
                  Cancel Subscription
                </Button>
              </>
            )}
            {(isCanceling || isCanceled) && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => openConfirmDialog('reactivate')}
                disabled={processing}
              >
                Reactivate Subscription
              </Button>
            )}
          </Box>

          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            mt={2}
          >
            Subscription ID: {subscription.subscriptionId}
          </Typography>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !processing && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmAction === 'cancel'
            ? 'Cancel Subscription'
            : confirmAction === 'reactivate'
            ? 'Reactivate Subscription'
            : 'Confirm Plan Change'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'cancel' ? (
              <>
                Are you sure you want to cancel your subscription? You will
                retain access until {periodEnd}, but{' '}
                <strong>no refund will be issued</strong> for the current
                billing period.
              </>
            ) : confirmAction === 'reactivate' ? (
              <>
                Are you sure you want to reactivate your subscription? It will
                continue automatically at the end of the current period.
              </>
            ) : (
              <>
                {selectedPlan && currentPlan && prorationPreviewData && (
                  <Box>
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                      <strong>Subscription Change Summary</strong>
                    </Typography>

                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" gutterBottom>
                        <strong>Current Plan:</strong> {currentPlan.name} - $
                        {currentPlan.price}/{currentPlan.term}
                      </Typography>
                      <Typography variant="body2">
                        <strong>New Plan:</strong> {selectedPlan.name} - $
                        {selectedPlan.price}/{selectedPlan.term}
                      </Typography>
                    </Box>

                    {/* Scheduled Downgrade Notice */}
                    {prorationPreviewData.isScheduledDowngrade ? (
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: 'rgba(255, 152, 0, 0.08)',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'warning.main',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          color="warning.main"
                        >
                          <strong>⏰ Scheduled Downgrade</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Your yearly subscription will continue until{' '}
                          <strong>
                            {new Date(
                              prorationPreviewData.scheduledChangeDate
                            ).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </strong>
                          .
                        </Typography>
                        <Typography variant="body2">
                          After that date, you'll automatically be switched to
                          the monthly plan at ${selectedPlan.price}/month.
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, fontStyle: 'italic' }}
                        >
                          No charge today. You can cancel this scheduled change
                          anytime from your subscription settings.
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: prorationPreviewData.isUpgrade
                            ? 'rgba(2, 136, 209, 0.08)'
                            : 'rgba(46, 125, 50, 0.08)',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: prorationPreviewData.isUpgrade
                            ? 'info.main'
                            : 'success.main',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          color={
                            prorationPreviewData.isUpgrade
                              ? 'info.main'
                              : 'success.main'
                          }
                        >
                          <strong>
                            {prorationPreviewData.isUpgrade
                              ? '↑ Upgrade - Immediate Charge'
                              : '↓ Downgrade - Credit Applied'}
                          </strong>
                        </Typography>

                        <Box
                          component="table"
                          sx={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            mt: 1,
                          }}
                        >
                          <tbody>
                            <Box
                              component="tr"
                              sx={{
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <td style={{ padding: '8px 0' }}>
                                <Typography variant="body2">
                                  Credit for unused {currentPlan.name}
                                </Typography>
                              </td>
                              <td
                                style={{ padding: '8px 0', textAlign: 'right' }}
                              >
                                <Typography variant="body2">
                                  -$
                                  {prorationPreviewData.creditAmount.toFixed(2)}
                                </Typography>
                              </td>
                            </Box>
                            <Box
                              component="tr"
                              sx={{
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <td style={{ padding: '8px 0' }}>
                                <Typography variant="body2">
                                  Prorated {selectedPlan.name} charge
                                </Typography>
                              </td>
                              <td
                                style={{ padding: '8px 0', textAlign: 'right' }}
                              >
                                <Typography variant="body2">
                                  $
                                  {prorationPreviewData.chargeAmount.toFixed(2)}
                                </Typography>
                              </td>
                            </Box>
                            <Box
                              component="tr"
                              sx={{
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <td style={{ padding: '8px 0' }}>
                                <Typography variant="body2" fontWeight="bold">
                                  Subtotal
                                </Typography>
                              </td>
                              <td
                                style={{ padding: '8px 0', textAlign: 'right' }}
                              >
                                <Typography variant="body2" fontWeight="bold">
                                  $
                                  {Math.abs(
                                    prorationPreviewData.prorationAmount
                                  ).toFixed(2)}
                                </Typography>
                              </td>
                            </Box>
                            {prorationPreviewData.availableCredit > 0 &&
                              prorationPreviewData.isUpgrade && (
                                <Box
                                  component="tr"
                                  sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    color: 'success.main',
                                  }}
                                >
                                  <td style={{ padding: '8px 0' }}>
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                    >
                                      Account credit applied
                                    </Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: '8px 0',
                                      textAlign: 'right',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                    >
                                      -$
                                      {prorationPreviewData.availableCredit.toFixed(
                                        2
                                      )}
                                    </Typography>
                                  </td>
                                </Box>
                              )}
                            <Box
                              component="tr"
                              sx={{
                                borderTop: '2px solid',
                                borderColor: 'text.primary',
                              }}
                            >
                              <td style={{ padding: '8px 0' }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {prorationPreviewData.isUpgrade
                                    ? 'Final charge today'
                                    : prorationPreviewData.availableCredit > 0
                                    ? 'Total credit on account'
                                    : 'Credit applied to next invoice'}
                                </Typography>
                              </td>
                              <td
                                style={{ padding: '8px 0', textAlign: 'right' }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color={
                                    prorationPreviewData.isUpgrade
                                      ? 'error.main'
                                      : 'success.main'
                                  }
                                >
                                  $
                                  {prorationPreviewData.isUpgrade
                                    ? prorationPreviewData.finalCharge.toFixed(
                                        2
                                      )
                                    : (
                                        Math.abs(
                                          prorationPreviewData.prorationAmount
                                        ) + prorationPreviewData.availableCredit
                                      ).toFixed(2)}
                                </Typography>
                              </td>
                            </Box>
                          </tbody>
                        </Box>

                        {prorationPreviewData.availableCredit > 0 && (
                          <Alert severity="success" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              You have{' '}
                              <strong>
                                $
                                {prorationPreviewData.availableCredit.toFixed(
                                  2
                                )}
                              </strong>{' '}
                              in account credit
                              {prorationPreviewData.isUpgrade
                                ? ' that will be applied to this charge.'
                                : ' that will be added to your new credit balance.'}
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    )}

                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>No Refunds:</strong> Subscription changes are
                        prorated. No cash refunds are issued. Credits are
                        applied to future billing.
                      </Typography>
                    </Alert>

                    <Typography variant="caption" color="textSecondary">
                      * Amounts are estimated and prorated based on time
                      remaining in your current billing cycle. Actual charges
                      may vary slightly.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color={confirmAction === 'cancel' ? 'error' : 'primary'}
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog
        open={changePlanDialogOpen}
        onClose={() => !processing && setChangePlanDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Change Subscription Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Select a new subscription plan. Changes are prorated based on your
            current billing cycle.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <RadioGroup
            value={selectedPlanId}
            onChange={(e) => handlePlanSelection(e.target.value)}
          >
            {availablePlans.map((plan) => {
              const isCurrent = plan.name === subscription?.planName;
              return (
                <Card
                  key={plan.id}
                  sx={{
                    mb: 2,
                    border: selectedPlanId === plan.id ? 2 : 1,
                    borderColor:
                      selectedPlanId === plan.id ? 'primary.main' : 'divider',
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      value={plan.id}
                      control={<Radio />}
                      disabled={isCurrent}
                      label={
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6">{plan.name}</Typography>
                            {isCurrent && (
                              <Chip
                                label="Current Plan"
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>
                          <Typography variant="body1" color="textSecondary">
                            ${plan.price} / {plan.term}
                          </Typography>
                          {plan.description && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {plan.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              );
            })}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setChangePlanDialogOpen(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmAction('change');
              setConfirmDialogOpen(true);
            }}
            variant="contained"
            disabled={!selectedPlanId || processing}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
